import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, ArrowLeft, ArrowRight, FileText, Clock, ShieldCheck, CaretDown, Target, Wrench, MapPinLine, ListChecks, Package, Star } from "@phosphor-icons/react";
import { fetchServiceDetail, fetchServices } from "@/lib/api";
import { SERVICE_GALLERY, IMG } from "@/lib/images";
import NorthAmericaMap from "@/components/NorthAmericaMap";
import CTASection from "@/components/CTASection";
import PageHero from "@/components/PageHero";

// Module-level Framer Motion configs to avoid re-creating objects each render.
const SCALE_IN_INITIAL = { opacity: 0, scale: 0.95 };
const SCALE_IN_ANIMATE = { opacity: 1, scale: 1 };
const SCALE_IN_TRANSITION = { duration: 0.6 };
const FADE_UP_INITIAL = { opacity: 0, y: 20 };
const FADE_UP_ANIMATE = { opacity: 1, y: 0 };
const VIEWPORT_ONCE = { once: true };

export default function ServiceDetail() {
    const { slug } = useParams();
    const [service, setService] = useState(null);
    const [allServices, setAllServices] = useState([]);
    const [error, setError] = useState(false);

    useEffect(() => {
        setError(false);
        setService(null);
        fetchServiceDetail(slug)
            .then((s) => setService(s))
            .catch(() => setError(true));
        fetchServices().then(setAllServices).catch(() => {});
    }, [slug]);

    useEffect(() => {
        if (!service) return;
        const seo = service.seo || {};
        document.title = seo.title || `Surveys2CAD | ${service.name}`;
        // Meta description
        const setMeta = (name, content) => {
            if (!content) return;
            let el = document.querySelector(`meta[name='${name}']`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute("name", name);
                document.head.appendChild(el);
            }
            el.setAttribute("content", content);
        };
        const setOG = (prop, content) => {
            if (!content) return;
            let el = document.querySelector(`meta[property='${prop}']`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute("property", prop);
                document.head.appendChild(el);
            }
            el.setAttribute("content", content);
        };
        const desc = seo.description || `${service.name} CAD drafting services — ${service.short}`;
        setMeta("description", desc);
        setMeta("keywords", (service.keywords || []).join(", "));
        setOG("og:title", seo.title || `${service.name} — Surveys2CAD`);
        setOG("og:description", desc);
        setOG("og:type", "article");
        setOG("og:url", window.location.href);
        setOG("og:site_name", "Surveys2CAD");
        setOG("og:locale", "en_US");

        // Hero image for link previews (LinkedIn / Slack / Twitter / WhatsApp).
        // Uses the service's gallery first image when available, else the site logo.
        const heroImg = (service.gallery && service.gallery[0])
            ? service.gallery[0]
            : `${window.location.origin}/icons/admin-icon-512.png`;
        setOG("og:image", heroImg);
        setOG("og:image:alt", `${service.name} — Surveys2CAD CAD drafting sample`);
        setOG("og:image:width", "1200");
        setOG("og:image:height", "630");

        // Twitter Card — large-image variant for maximum B2B referral visibility.
        const setTW = (name, content) => {
            let el = document.querySelector(`meta[name='${name}']`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute("name", name);
                document.head.appendChild(el);
            }
            el.setAttribute("content", content);
        };
        setTW("twitter:card", "summary_large_image");
        setTW("twitter:title", seo.title || `${service.name} — Surveys2CAD`);
        setTW("twitter:description", desc);
        setTW("twitter:image", heroImg);
        setTW("twitter:image:alt", `${service.name} — Surveys2CAD`);
        // Canonical link
        let canonical = document.querySelector("link[rel='canonical']");
        if (!canonical) {
            canonical = document.createElement("link");
            canonical.setAttribute("rel", "canonical");
            document.head.appendChild(canonical);
        }
        canonical.setAttribute("href", window.location.href.split("?")[0]);

        // -----------------------------------------------------------------
        // JSON-LD structured data: Service + FAQPage + BreadcrumbList
        // Google will render the FAQ answers directly in search results and
        // treat this page as a first-class Service node in the knowledge graph.
        // -----------------------------------------------------------------
        const origin = window.location.origin;
        const pageUrl = `${origin}/services/${service.slug}`;
        const faqs = service.content?.faqs || [];
        const includedItems = service.content?.whats_included || service.deliverables || [];
        const imageUrls = (service.gallery || []).length
            ? service.gallery
            : [origin + "/icons/admin-icon-512.png"];

        const serviceJsonLd = {
            "@context": "https://schema.org",
            "@type": "Service",
            "@id": pageUrl + "#service",
            "name": service.name,
            "description": service.content?.about || service.short,
            "provider": {
                "@type": "Organization",
                "name": "Surveys2CAD",
                "url": origin,
                "logo": origin + "/icons/admin-icon-512.png",
            },
            "areaServed": [
                { "@type": "Country", "name": "United States" },
                { "@type": "Country", "name": "Canada" },
            ],
            "serviceType": service.name,
            "url": pageUrl,
            "image": imageUrls,
            "offers": {
                "@type": "Offer",
                "priceCurrency": "USD",
                "price": service.base_rate || undefined,
                "availability": "https://schema.org/InStock",
                "url": origin + "/get-started",
            },
            "hasOfferCatalog": {
                "@type": "OfferCatalog",
                "name": `${service.name} — What's Included`,
                "itemListElement": includedItems.slice(0, 12).map((item, i) => ({
                    "@type": "Offer",
                    "itemOffered": { "@type": "Service", "name": item },
                    "position": i + 1,
                })),
            },
        };

        const faqJsonLd = faqs.length > 0 ? {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map((f) => ({
                "@type": "Question",
                "name": f.q,
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": f.a,
                },
            })),
        } : null;

        const breadcrumbJsonLd = {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": origin + "/" },
                { "@type": "ListItem", "position": 2, "name": "Services", "item": origin + "/services" },
                { "@type": "ListItem", "position": 3, "name": service.name, "item": pageUrl },
            ],
        };

        // Remove any prior JSON-LD blocks added by this page (navigate-between-services)
        document.querySelectorAll("script[data-s2c-jsonld]").forEach((n) => n.remove());
        [serviceJsonLd, faqJsonLd, breadcrumbJsonLd].filter(Boolean).forEach((obj, i) => {
            const s = document.createElement("script");
            s.type = "application/ld+json";
            s.setAttribute("data-s2c-jsonld", String(i));
            s.text = JSON.stringify(obj);
            document.head.appendChild(s);
        });

        return () => {
            document.querySelectorAll("script[data-s2c-jsonld]").forEach((n) => n.remove());
        };
    }, [service]);

    if (error) {
        return (
            <div className="max-w-3xl mx-auto px-5 lg:px-8 py-24 text-center">
                <div className="tech-card p-8" data-testid="service-error">
                    <h1 className="font-heading text-2xl text-slate-50 mb-2">Service not found</h1>
                    <p className="text-slate-400 mb-6">
                        We couldn't load this service. It may have been renamed or temporarily unavailable.
                    </p>
                    <Link to="/services" className="btn-primary" data-testid="back-to-services-error">
                        Back to all services <ArrowRight size={16} weight="bold" />
                    </Link>
                </div>
            </div>
        );
    }

    if (!service) {
        return (
            <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24" data-testid="service-loading">
                <div className="max-w-3xl animate-pulse">
                    <div className="h-4 w-32 bg-slate-800/70 rounded mb-6" />
                    <div className="h-14 w-2/3 bg-slate-800/60 rounded mb-4" />
                    <div className="h-4 w-full bg-slate-800/50 rounded mb-2" />
                    <div className="h-4 w-5/6 bg-slate-800/50 rounded mb-8" />
                    <div className="flex gap-3">
                        <div className="h-12 w-40 bg-slate-800/60 rounded-full" />
                        <div className="h-12 w-40 bg-slate-800/50 rounded-full" />
                    </div>
                </div>
            </div>
        );
    }

    const gallery = SERVICE_GALLERY[service.slug] || [IMG.blueprintCad, IMG.droneSite, IMG.surveyorField];
    const others = allServices.filter((s) => s.slug !== service.slug).slice(0, 4);

    return (
        <div>
            {/* Hero — service-detail variant (contour + compass) */}
            <PageHero variant="service-detail" tint="cyan">
                <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-12 pb-8">
                    <Link to="/services" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-300" data-testid="back-to-services">
                        <ArrowLeft size={16} /> All services
                    </Link>
                </div>
                <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-5 gap-10 pb-16">
                <div className="lg:col-span-3">
                    <div className="overline mb-3">{service.keywords?.[0]}</div>
                    <h1 className="font-heading text-5xl sm:text-6xl font-bold text-slate-50 tracking-tighter leading-[1.02]" data-testid="service-title">
                        {service.name}
                    </h1>
                    <p className="text-lg text-slate-400 mt-6 leading-relaxed max-w-2xl">{service.description}</p>
                    <div className="flex flex-wrap gap-3 mt-8">
                        <Link to="/get-started" className="btn-primary" data-testid="service-quote-btn">
                            Get a quote <ArrowRight size={16} weight="bold" />
                        </Link>
                        <Link to="/contact" className="btn-ghost">
                            Talk to a specialist
                        </Link>
                    </div>
                    <div className="flex flex-wrap gap-5 mt-8 text-xs font-mono-alt text-slate-400">
                        <span className="flex items-center gap-2"><Clock size={14} className="text-cyan-400" /> 24–72 hr turnaround</span>
                        <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-cyan-400" /> Peer-reviewed QA</span>
                        <span className="flex items-center gap-2"><FileText size={14} className="text-cyan-400" /> Native DWG + stamped PDF</span>
                    </div>
                </div>
                <div className="lg:col-span-2">
                    <motion.img
                        src={gallery[0]}
                        alt={service.name}
                        initial={SCALE_IN_INITIAL}
                        animate={SCALE_IN_ANIMATE}
                        transition={SCALE_IN_TRANSITION}
                        className="w-full rounded-2xl border border-slate-800 object-cover aspect-[4/5]"
                        data-testid="service-hero-image"
                    />
                </div>
                </div>
            </PageHero>

            {/* 1. About This Service — amber */}
            {service.content?.about && (
                <section className="bg-section-amber section-divider relative" data-testid="about-section">
                    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-3 gap-10">
                        <div className="lg:col-span-1">
                            <div className="overline mb-3 !text-orange-400">01 · About</div>
                            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-50 flex items-start gap-3">
                                <Target size={28} weight="fill" className="text-orange-400 mt-1 shrink-0" />
                                About This Service
                            </h2>
                        </div>
                        <div className="lg:col-span-2">
                            <p className="text-slate-300 leading-relaxed text-lg">{service.content.about}</p>
                            <div className="grid grid-cols-3 gap-4 mt-8">
                                {service.content.key_features?.slice(0, 3).map((f) => (
                                    <div key={f} className="tech-card p-4 text-center">
                                        <Star size={16} weight="fill" className="text-cyan-400 mx-auto mb-2" />
                                        <div className="text-xs text-slate-300 leading-snug">{f}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* 2. What's Included — cyan */}
            {service.content?.whats_included?.length > 0 && (
                <section className="bg-section-cyan section-divider relative" data-testid="whats-included-section">
                    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
                        <div className="overline mb-3">02 · Scope</div>
                        <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-50 mb-3">
                            What's Included
                        </h2>
                        <p className="text-slate-400 max-w-3xl mb-10">
                            Every {service.name} engagement includes the items below — no hidden change orders.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {service.content.whats_included.map((item) => (
                                <div key={item} className="tech-card p-4 flex items-start gap-3">
                                    <CheckCircle size={20} weight="fill" className="text-cyan-400 flex-shrink-0 mt-0.5" />
                                    <span className="text-slate-200 leading-relaxed text-[15px]">{item}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* 3. What We Cover — violet */}
            {service.content?.what_we_cover?.length > 0 && (
                <section className="bg-section-violet section-divider relative" data-testid="what-we-cover-section">
                    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
                        <div className="overline mb-3">03 · Coverage</div>
                        <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-50 mb-3">
                            What We Cover
                        </h2>
                        <p className="text-slate-400 max-w-3xl mb-10">
                            Project types and site categories we routinely deliver {service.name.toLowerCase()} drafting for.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {service.content.what_we_cover.map((item, i) => (
                                <div key={item} className="tech-card p-5 hover:border-violet-500/40 transition-colors">
                                    <div className="flex items-center gap-2 mb-2">
                                        <MapPinLine size={16} weight="fill" className="text-violet-400" />
                                        <div className="overline !text-violet-400 text-[10px]">Type {String(i + 1).padStart(2, "0")}</div>
                                    </div>
                                    <div className="text-slate-200 text-sm leading-relaxed">{item}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* 4. Our Process — emerald */}
            {service.content?.our_process?.length > 0 && (
                <section className="bg-section-emerald section-divider relative" data-testid="our-process-section">
                    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
                        <div className="overline mb-3 !text-emerald-400">04 · Workflow</div>
                        <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-50 mb-3">
                            Our Process
                        </h2>
                        <p className="text-slate-400 max-w-3xl mb-12">
                            A proven 5-step CAD drafting workflow — from intake to revisions.
                        </p>
                        <div className="relative grid grid-cols-1 md:grid-cols-5 gap-4">
                            {service.content.our_process.map((step, i) => (
                                <div key={step.step} className="tech-card p-5 relative" data-testid={`process-step-${i + 1}`}>
                                    <div className="absolute -top-3 -left-3 w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-400/60 flex items-center justify-center text-emerald-300 font-mono-alt font-bold text-sm">
                                        {step.step}
                                    </div>
                                    <div className="mt-3 font-heading font-semibold text-slate-50 text-base mb-2">{step.title}</div>
                                    <div className="text-xs text-slate-400 leading-relaxed">{step.body}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* 5. Getting Started — orange */}
            {service.content?.getting_started?.length > 0 && (
                <section className="bg-section-amber section-divider relative" data-testid="getting-started-section">
                    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-3 gap-10">
                        <div>
                            <div className="overline mb-3 !text-orange-400">05 · Kickoff</div>
                            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-50 flex items-start gap-3">
                                <Wrench size={26} weight="fill" className="text-orange-400 mt-1 shrink-0" />
                                Getting Started
                            </h2>
                            <p className="text-slate-400 mt-4 leading-relaxed">
                                Documents we need to start your {service.name.toLowerCase()} project. Missing something?
                                We'll still start — just let us know and we'll work around it.
                            </p>
                            <Link to="/get-started" className="btn-primary mt-6 inline-flex" data-testid="getting-started-cta">
                                Send us these files <ArrowRight size={14} weight="bold" />
                            </Link>
                        </div>
                        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {service.content.getting_started.map((item, i) => (
                                <div key={item} className="tech-card p-4 flex items-start gap-3">
                                    <div className="w-7 h-7 rounded-full bg-orange-500/15 border border-orange-400/40 text-orange-300 flex items-center justify-center text-[11px] font-mono-alt font-bold shrink-0">
                                        {String(i + 1).padStart(2, "0")}
                                    </div>
                                    <span className="text-slate-200 text-sm leading-relaxed">{item}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* 6. What We Deliver — cyan */}
            {service.content?.what_we_deliver?.length > 0 && (
                <section className="bg-section-cyan section-divider relative" data-testid="what-we-deliver-section">
                    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
                        <div className="overline mb-3">06 · Deliverables</div>
                        <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-50 mb-3 flex items-center gap-3">
                            <Package size={28} weight="fill" className="text-cyan-400" />
                            What We Deliver
                        </h2>
                        <p className="text-slate-400 max-w-3xl mb-10">
                            Every package is native-editable, peer-QA'd, and shipped as a single signed ZIP.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {service.content.what_we_deliver.map((item) => (
                                <div key={item} className="flex items-start gap-3 p-3 rounded-lg border border-slate-800 bg-slate-900/40 hover:border-cyan-500/40 transition-colors">
                                    <ListChecks size={18} weight="bold" className="text-cyan-400 mt-0.5 shrink-0" />
                                    <span className="text-slate-200 text-sm leading-relaxed">{item}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* 7. Key Features — violet */}
            {service.content?.key_features?.length > 0 && (
                <section className="bg-section-violet section-divider relative" data-testid="key-features-section">
                    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
                        <div className="overline mb-3">07 · Features</div>
                        <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-50 mb-3">
                            Key Features
                        </h2>
                        <p className="text-slate-400 max-w-3xl mb-10">
                            The baseline commitments that apply to every {service.name} project.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {service.content.key_features.map((feat) => (
                                <div key={feat} className="tech-card p-5">
                                    <Star size={20} weight="fill" className="text-cyan-400 mb-3" />
                                    <div className="text-slate-100 font-heading text-sm leading-snug">{feat}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* (existing Deliverables + keywords) */}
            <section className="bg-section-violet section-divider relative">
                <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12">
                    <div>
                        <div className="overline mb-3">What you receive</div>
                        <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-50 mb-6">Deliverables</h2>
                        <ul className="space-y-3" data-testid="deliverables-list">
                            {service.deliverables?.map((d) => (
                                <li key={d} className="flex items-start gap-3 tech-card p-4">
                                    <CheckCircle size={20} weight="fill" className="text-cyan-400 flex-shrink-0 mt-0.5" />
                                    <span className="text-slate-200">{d}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <div className="overline mb-3">SEO expertise — keywords we rank for</div>
                        <h2 className="font-heading text-2xl md:text-3xl font-semibold text-slate-50 mb-2">
                            Firms find us when they search for…
                        </h2>
                        <p className="text-slate-400 text-sm mb-6 leading-relaxed">
                            {service.keywords?.length || 0}+ primary keywords — covering software variants (AutoCAD / Civil 3D / Revit), deliverable types (DWG / DXF / PDF / point files), buyer intent ("near me", "outsourced"), and industry-specific compliance terms.
                        </p>
                        <div className="flex flex-wrap gap-2" data-testid="service-keywords">
                            {service.keywords?.map((k, i) => {
                                let tierClass;
                                if (i < 3) {
                                    tierClass = "text-cyan-200 border border-cyan-400/50 bg-cyan-500/15 font-semibold";
                                } else if (i < 8) {
                                    tierClass = "text-slate-200 border border-slate-700 bg-slate-900/60";
                                } else {
                                    tierClass = "text-slate-400 border border-slate-800 bg-slate-900/30";
                                }
                                return (
                                    <span
                                        key={k}
                                        className={`px-3 py-1.5 rounded-full text-xs font-mono-alt transition-colors ${tierClass}`}
                                        data-testid={`kw-${k.replace(/\s+/g, "-")}`}
                                    >
                                        {i < 3 && <span className="mr-1 text-orange-400">★</span>}
                                        {k}
                                    </span>
                                );
                            })}
                        </div>
                        <div className="flex items-center gap-4 mt-3 text-[10px] font-mono-alt text-slate-500">
                            <span><span className="text-orange-400">★</span> High-intent primary</span>
                            <span><span className="text-slate-300">•</span> Industry variants</span>
                            <span><span className="text-slate-500">•</span> Long-tail SEO</span>
                        </div>
                        <div className="tech-card p-6 mt-8">
                            <div className="overline mb-2">Starting at</div>
                            <div className="text-4xl font-heading font-bold text-slate-50 font-mono-alt">
                                ${service.base_rate?.toLocaleString()}
                            </div>
                            <div className="text-sm text-slate-400 mt-1">
                                + ${service.rate_per_acre}/acre for additional acreage
                            </div>
                            <Link to="/get-started" className="btn-primary w-full justify-center mt-5">
                                Request exact quote <ArrowRight size={14} weight="bold" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Portfolio gallery */}
            <section className="bg-section-amber section-divider relative" data-testid="portfolio-gallery">
                <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
                <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                    <div>
                        <div className="overline mb-3">Portfolio</div>
                        <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-50">
                            Representative <span className="text-cyan-400">{service.name}</span> work.
                        </h2>
                    </div>
                    <div className="text-sm text-slate-500 font-mono-alt">Client names redacted under NDA</div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {gallery.map((src, i) => (
                        <motion.div
                            key={src}
                            initial={FADE_UP_INITIAL}
                            whileInView={FADE_UP_ANIMATE}
                            viewport={VIEWPORT_ONCE}
                            transition={{ delay: i * 0.1 }}
                            className="relative group overflow-hidden rounded-2xl border border-slate-800"
                            data-testid={`portfolio-item-${i}`}
                        >
                            <img src={src} alt={`${service.name} portfolio ${i + 1}`} className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-500" />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#030712]/95 via-transparent to-transparent opacity-70 group-hover:opacity-90 transition-opacity" />
                            <div className="absolute bottom-0 left-0 p-5">
                                <div className="overline">Project #{(i + 1).toString().padStart(3, "0")}</div>
                                <div className="font-heading text-lg text-slate-50 mt-1">{service.name} — Case {i + 1}</div>
                            </div>
                        </motion.div>
                    ))}
                </div>
                </div>
            </section>

            {/* Coverage map — emerald */}
            <section className="bg-section-emerald section-divider relative">
                <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
                    <div className="overline mb-3">Geographic coverage</div>
                    <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-50 mb-3">
                        Delivered across USA & Canada.
                    </h2>
                    <p className="text-slate-400 max-w-2xl mb-8">
                        {service.name} drafting delivered in every US state and Canadian province — with drafters familiar
                        with local coordinate systems, recording standards, and regional compliance.
                    </p>
                    <NorthAmericaMap />
                </div>
            </section>

            {/* Other services — rose */}
            <section className="bg-section-rose section-divider relative">
                <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
                    <div className="overline mb-3">You might also need</div>
                    <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-50 mb-8">Related services</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {others.map((o) => (
                            <Link
                                key={o.slug}
                                to={`/services/${o.slug}`}
                                className="tech-card p-5 block"
                                data-testid={`related-${o.slug}`}
                            >
                                <div className="font-heading font-semibold text-slate-50 text-base">{o.name}</div>
                                <p className="text-xs text-slate-400 mt-2 leading-relaxed">{o.short}</p>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* 8. FAQs — rose (at the very end per spec) */}
            {service.content?.faqs?.length > 0 && (
                <section className="bg-section-rose section-divider relative" data-testid="faqs-section">
                    <div className="max-w-4xl mx-auto px-5 lg:px-8 py-20">
                        <div className="overline mb-3">08 · Answers</div>
                        <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-50 mb-3">
                            Frequently Asked Questions
                        </h2>
                        <p className="text-slate-400 mb-10">
                            The most common questions we get on {service.name.toLowerCase()} projects.
                        </p>
                        <div className="space-y-3">
                            {service.content.faqs.map((item, i) => (
                                <FaqItem key={item.q} q={item.q} a={item.a} index={i} defaultOpen={i === 0} />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
                <CTASection title={`Let's ship your next ${service.name} draft.`} />
            </section>
        </div>
    );
}

function FaqItem({ q, a, index, defaultOpen }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div
            className={`tech-card overflow-hidden transition-colors ${open ? "!border-cyan-500/40" : ""}`}
            data-testid={`faq-item-${index}`}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-start justify-between gap-4 p-5 text-left"
                aria-expanded={open}
            >
                <span className="font-heading font-semibold text-slate-100 text-base">{q}</span>
                <CaretDown
                    size={18}
                    weight="bold"
                    className={`shrink-0 mt-0.5 text-cyan-400 transition-transform ${open ? "rotate-180" : ""}`}
                />
            </button>
            {open && (
                <div className="px-5 pb-5 -mt-1 text-slate-300 text-[15px] leading-relaxed border-t border-slate-800/60 pt-4">
                    {a}
                </div>
            )}
        </div>
    );
}
